<!--
  @component ResponsiveLayout
  @description Manages responsive breakpoints and mobile navigation state

  Provides responsive context to child components:
  - isMobile: boolean (viewport <= 767px)
  - isTablet: boolean (viewport 768-1023px)
  - windowWidth: number (reactive window width)
  - mobileView: 'collections' | 'items' | 'details'
  - showMobileCollections: boolean

  Dispatches events:
  - viewChange: { view: MobileView }
  - toggleCollections: { show: boolean }
-->
<script lang="ts" context="module">
  export type MobileView = 'collections' | 'items' | 'details';

  export interface ResponsiveContext {
    isMobile: boolean;
    isTablet: boolean;
    windowWidth: number;
    mobileView: MobileView;
    showMobileCollections: boolean;
    navigateTo: (view: MobileView) => void;
    toggleCollections: () => void;
    closeCollections: () => void;
  }

  export const RESPONSIVE_CONTEXT_KEY = Symbol('responsive');
</script>

<script lang="ts">
  import { onMount, onDestroy, setContext, createEventDispatcher } from 'svelte';
  import { writable } from 'svelte/store';
  import { selectedItem } from '@stores/itemStore';

  // ==========================================================================
  // CONSTANTS
  // ==========================================================================

  const MOBILE_BREAKPOINT = 767;
  const TABLET_BREAKPOINT = 1023;

  // ==========================================================================
  // STATE
  // ==========================================================================

  let isMobile = false;
  let isTablet = false;
  let windowWidth = typeof window !== 'undefined' ? window.innerWidth : 1024;
  let mobileView: MobileView = 'items';
  let showMobileCollections = false;

  const dispatch = createEventDispatcher<{
    viewChange: { view: MobileView };
    toggleCollections: { show: boolean };
  }>();

  // ==========================================================================
  // VIEWPORT DETECTION
  // ==========================================================================

  function checkViewport() {
    windowWidth = window.innerWidth;
    isMobile = windowWidth <= MOBILE_BREAKPOINT;
    isTablet = windowWidth > MOBILE_BREAKPOINT && windowWidth <= TABLET_BREAKPOINT;

    // Reset mobile collections overlay when switching to desktop
    if (!isMobile && !isTablet) {
      showMobileCollections = false;
    }
  }

  // ==========================================================================
  // NAVIGATION FUNCTIONS
  // ==========================================================================

  function navigateTo(view: MobileView) {
    mobileView = view;
    showMobileCollections = false;
    dispatch('viewChange', { view });
  }

  function toggleCollections() {
    showMobileCollections = !showMobileCollections;
    dispatch('toggleCollections', { show: showMobileCollections });
  }

  function closeCollections() {
    showMobileCollections = false;
    dispatch('toggleCollections', { show: false });
  }

  // ==========================================================================
  // AUTO-NAVIGATION
  // ==========================================================================

  // Auto-navigate to details when item is selected on mobile
  $: if (isMobile && $selectedItem && mobileView === 'items') {
    mobileView = 'details';
  }

  // ==========================================================================
  // CONTEXT
  // ==========================================================================

  // Create a reactive store for the context
  const contextStore = writable<ResponsiveContext>({
    isMobile,
    isTablet,
    windowWidth,
    mobileView,
    showMobileCollections,
    navigateTo,
    toggleCollections,
    closeCollections,
  });

  // Update context when state changes
  $: $contextStore = {
    isMobile,
    isTablet,
    windowWidth,
    mobileView,
    showMobileCollections,
    navigateTo,
    toggleCollections,
    closeCollections,
  };

  setContext(RESPONSIVE_CONTEXT_KEY, contextStore);

  // ==========================================================================
  // LIFECYCLE
  // ==========================================================================

  onMount(() => {
    checkViewport();
    window.addEventListener('resize', checkViewport);
  });

  onDestroy(() => {
    if (typeof window !== 'undefined') {
      window.removeEventListener('resize', checkViewport);
    }
  });

  // ==========================================================================
  // EXPORTS
  // ==========================================================================

  export { isMobile, isTablet, windowWidth, mobileView, showMobileCollections };
  export { navigateTo, toggleCollections, closeCollections };
</script>

<div class="responsive-layout" class:mobile={isMobile} class:tablet={isTablet}>
  <slot
    {isMobile}
    {isTablet}
    {windowWidth}
    {mobileView}
    {showMobileCollections}
    {navigateTo}
    {toggleCollections}
    {closeCollections}
  />
</div>

<style>
  .responsive-layout {
    display: contents;
  }
</style>
